import { FC, useState, useEffect, useRef } from "react";
import styles from "./CreateFolderModal.module.scss";
import { CloseIcon } from "../../../assets/svg/CloseIcon";
import { useAppDispatch, useAppSelector, RootState } from "../../../redux/store";
import { useStore } from "react-redux";
import { createTemplateFolderThunk, createTemplateThunk, updateTemplateThunk, updateTemplateFolderThunk, fetchTemplateFoldersThunk, deleteTemplateFolderThunk } from "../../../redux/reducers/ui/uiThunks";
import { getTemplateFolders } from "../../../redux/reducers/ui/uiSelectors";
import { getConfigInfo } from "../../../utils/functions/playcanvas/getConfigInfo";
import { getDataForTemplateConfig } from "../../../redux/reducers/ui/selectors/templateSelectors";
import PlayCanvasService from "../../../services/PlayCanvasService/PlayCanvasService";
import { popUp } from "../../../features/popUp/PopUp";
import { ITemplate, IObject } from "../../../redux/reducers/ui/uiTypes";
import { DropDownIcon } from "../../../assets/svg/DropDownIcon";
import { useURLRouting } from "../../../utils/hooks/useURLRouting";

type CreateFolderModalProps = {
  closeFunc: () => void;
  initialData?: {
    isEdit?: boolean;
    isDelete?: boolean;
    template?: ITemplate;
    folder?: IObject;
  };
};

export const CreateFolderModal: FC<CreateFolderModalProps> = ({ closeFunc, initialData }) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const templateFolders = useAppSelector(getTemplateFolders());
  const { setTemplate } = useURLRouting();
  
  // Edit mode detection
  const isEdit = Boolean(initialData?.isEdit);
  const isDelete = Boolean(initialData?.isDelete);
  const templateToEdit = initialData?.template;
  const folderToEdit = initialData?.folder;
  
  // Template creation state
  const [templateName, setTemplateName] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [templateError, setTemplateError] = useState("");
  
  // Folder creation state
  const [folderName, setFolderName] = useState("");
  const [folderError, setFolderError] = useState("");
  
  // General state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFolderDropdownOpen, setIsFolderDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Reset form when modal opens or initialize for edit
  useEffect(() => {
    // Fetch latest folders when modal opens
    dispatch(fetchTemplateFoldersThunk({}));
    
    if (isEdit && templateToEdit) {
      // Initialize for editing template
      setTemplateName(templateToEdit.name);
      setSelectedFolderId(templateToEdit.folderId);
      setTemplateError("");
      setFolderName("");
      setFolderError("");
    } else if (isEdit && folderToEdit) {
      // Initialize for editing folder
      setFolderName(folderToEdit.name);
      setTemplateName("");
      setSelectedFolderId(null);
      setTemplateError("");
      setFolderError("");
    } else {
      // Reset form state for creation
      setTemplateName("");
      setSelectedFolderId(null);
      setTemplateError("");
      setFolderName("");
      setFolderError("");
    }
    
    setIsSubmitting(false);
    setIsFolderDropdownOpen(false);
  }, [dispatch, isEdit, templateToEdit, folderToEdit]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        closeFunc();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeFunc]);

  const validateTemplateName = (name: string): string => {
    if (!name.trim()) {
      return "Template name is required";
    }
    if (name.length > 50) {
      return "Template name must be less than 50 characters";
    }
    return "";
  };

  const validateFolderName = (name: string): string => {
    if (!name.trim()) {
      return "Folder name is required";
    }
    if (name.length > 50) {
      return "Folder name must be less than 50 characters";
    }
    return "";
  };

  const validateFolderSelection = (): string => {
    if (selectedFolderId === null) {
      return "Please select a folder";
    }
    // Check if selected folder still exists
    const folderExists = templateFolders.find(folder => folder.id === selectedFolderId);
    if (!folderExists) {
      return "Selected folder no longer exists";
    }
    return "";
  };

  const handleTemplateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemplateName(value);
    
    if (templateError) {
      setTemplateError("");
    }
  };

  const handleFolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFolderName(value);
    
    if (folderError) {
      setFolderError("");
    }
  };

  const handleFolderSelect = (folderId: number) => {
    setSelectedFolderId(folderId);
    setIsFolderDropdownOpen(false);
    if (templateError) {
      setTemplateError("");
    }
  };

  const handleSubmit = async () => {
    // Handle edit mode
    if (isEdit && templateToEdit) {
      const templateValidationError = validateTemplateName(templateName);
      const folderSelectionError = validateFolderSelection();
      
      if (templateValidationError) {
        setTemplateError(templateValidationError);
        return;
      }
      
      if (folderSelectionError) {
        setTemplateError(folderSelectionError);
        return;
      }

      setIsSubmitting(true);
      try {
        const raw = await getConfigInfo();
        const config = getDataForTemplateConfig(raw)(store.getState());
        const screenshotFile = PlayCanvasService.captureScreenshotFile();
        
        if (!screenshotFile) {
          throw new Error("Can't capture screenshot");
        }

        await dispatch(updateTemplateThunk({
          id: templateToEdit.id,
          name: templateName.trim(),
          config,
          imageFile: screenshotFile,
        })).unwrap();
        
        popUp({ type: "success", message: "Template updated successfully" });
        closeFunc();
      } catch (error: any) {
        popUp({ type: "failure", message: error.message || "Failed to update template" });
        console.error("Error updating template:", error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Handle folder edit mode
    if (isEdit && folderToEdit) {
      const folderValidationError = validateFolderName(folderName);
      if (folderValidationError) {
        setFolderError(folderValidationError);
        return;
      }

      setIsSubmitting(true);
      try {
        await dispatch(updateTemplateFolderThunk({
          folderId: folderToEdit.id,
          data: { name: folderName.trim() }
        })).unwrap();
        
        popUp({ type: "success", message: "Folder renamed successfully" });
        closeFunc();
      } catch (error: any) {
        popUp({ type: "failure", message: error.message || "Failed to rename folder" });
        console.error("Error renaming folder:", error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Handle folder delete mode
    if (isDelete && folderToEdit) {
      setIsSubmitting(true);
      try {
        await dispatch(deleteTemplateFolderThunk(folderToEdit.id)).unwrap();
        
        popUp({ type: "success", message: "Folder deleted successfully" });
        // Navigate back to folder list
        setTemplate('');
        closeFunc();
      } catch (error: any) {
        popUp({ type: "failure", message: error.message || "Failed to delete folder" });
        console.error("Error deleting folder:", error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Handle creation mode
    // Determine which scenario: creating template or creating folder
    const isCreatingFolder = folderName.trim().length > 0;
    const isCreatingTemplate = templateName.trim().length > 0 && selectedFolderId !== null;

    // Priority: folder creation first (if folder name is filled)
    if (isCreatingFolder) {
      // Validate folder creation only
      const folderValidationError = validateFolderName(folderName);
      if (folderValidationError) {
        setFolderError(folderValidationError);
        return;
      }

      setIsSubmitting(true);
      try {
        await dispatch(createTemplateFolderThunk({ 
          name: folderName.trim()
        })).unwrap();
        
        popUp({ type: "success", message: "Folder created successfully" });
        closeFunc();
      } catch (error: any) {
        popUp({ type: "failure", message: error.message || "Failed to create folder" });
        console.error("Error creating folder:", error);
      } finally {
        setIsSubmitting(false);
      }
    } else if (isCreatingTemplate) {
      // Validate template creation
      const templateValidationError = validateTemplateName(templateName);
      const folderSelectionError = validateFolderSelection();
      
      if (templateValidationError) {
        setTemplateError(templateValidationError);
        return;
      }
      
      if (folderSelectionError) {
        setTemplateError(folderSelectionError);
        return;
      }

      setIsSubmitting(true);
      try {
        // Gather config and screenshot like in CreateTemplateModal
        const raw = await getConfigInfo();
        const config = getDataForTemplateConfig(raw)(store.getState());
        const screenshotFile = PlayCanvasService.captureScreenshotFile();
        
        if (!screenshotFile) {
          throw new Error("Can't capture screenshot");
        }

        await dispatch(createTemplateThunk({
          name: templateName.trim(),
          folderId: selectedFolderId!,
          config,
          imageFile: screenshotFile,
        })).unwrap();
        
        popUp({ type: "success", message: "Template created successfully" });
        closeFunc();
      } catch (error: any) {
        popUp({ type: "failure", message: error.message || "Failed to create template" });
        console.error("Error creating template:", error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setTemplateError("Please fill template details or create a new folder");
      return;
    }
  };

  // Form validation for submit button
  const isCreatingFolder = folderName.trim().length > 0;
  
  const isTemplateFormValid = templateName.trim().length > 0 && selectedFolderId !== null && !templateError;
  const isFolderFormValid = folderName.trim().length > 0 && !folderError;
  
  // In edit mode, check which type of editing
  const isFormValid = isEdit 
    ? (templateToEdit ? (isTemplateFormValid && !isSubmitting) : (isFolderFormValid && !isSubmitting))
    : isDelete
    ? (!isSubmitting)
    : ((isCreatingFolder ? isFolderFormValid : isTemplateFormValid) && !isSubmitting);

  const selectedFolder = templateFolders.find(folder => folder.id === selectedFolderId);

  const getModalTitle = () => {
    if (isEdit && templateToEdit) return "Edit Template";
    if (isEdit && folderToEdit) return "Rename Folder";
    if (isDelete && folderToEdit) return "Delete Folder";
    return "Create Template or Folder";
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) {
      if (isEdit && templateToEdit) return 'Updating...';
      if (isEdit && folderToEdit) return 'Renaming...';
      if (isDelete && folderToEdit) return 'Deleting...';
      return isCreatingFolder ? 'Creating Folder...' : 'Creating Template...';
    }
    if (isEdit && templateToEdit) return 'Update Template';
    if (isEdit && folderToEdit) return 'Rename Folder';
    if (isDelete && folderToEdit) return 'Delete';
    return isCreatingFolder ? 'Create Folder' : 'Create Template';
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.head}>
        <span>{getModalTitle()}</span>
        <div className={styles.close} onClick={closeFunc}>
          <CloseIcon />
        </div>
      </div>

      {/* Template Creation/Edit Section - Only show when not editing a folder and not deleting */}
      {!(isEdit && folderToEdit) && !isDelete && (
        <div className={styles.inputSection}>
          <label htmlFor="templateName">Template Name <span className={styles.required}>*</span></label>
          <input
            type="text"
            id="templateName"
            name="templateName"
            value={templateName}
            onChange={handleTemplateNameChange}
            placeholder="Enter template name..."
            disabled={isSubmitting}
            required
            className={templateError ? styles.inputError : ''}
          />
          
          <label htmlFor="folderSelect" className={styles.folderSelectLabel}>
            Select Folder <span className={styles.required}>*</span>
          </label>
          <div className={styles.folderSelectWrapper}>
            <div 
              className={`${styles.folderSelect} ${isFolderDropdownOpen ? styles.active : ''} ${templateError && !selectedFolder ? styles.inputError : ''}`}
              onClick={() => setIsFolderDropdownOpen(!isFolderDropdownOpen)}
            >
              <span>{selectedFolder ? selectedFolder.name : 'Choose folder...'}</span>
             <DropDownIcon/>
            </div>
            
            {isFolderDropdownOpen && (
              <div className={styles.folderDropdownContent}>
                {templateFolders && templateFolders.length > 0 ? (
                  templateFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className={`${styles.folderOption} ${selectedFolderId === folder.id ? styles.active : ''}`}
                      onClick={() => handleFolderSelect(folder.id)}
                    >
                      {folder.name}
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyFolders}>No folders available</div>
                )}
              </div>
            )}
          </div>
          
          {templateError && <div className={styles.error}>{templateError}</div>}
        </div>
      )}

      {/* Folder Creation/Edit/Delete Section - Show when creating, editing, or deleting a folder */}
      {(!isEdit || folderToEdit || isDelete) && (
        <>
          {/* OR Divider - Only show when not in edit mode and not in delete mode */}
          {!isEdit && !isDelete && (
            <div className={styles.divider}>
              <span className={styles.dividerLine}></span>
              <span className={styles.dividerText}>Or create folder</span>
              <span className={styles.dividerLine}></span>
            </div>
          )}

          {/* Folder Creation/Edit/Delete Section */}
          <div className={styles.inputSection}>
            {isDelete && folderToEdit ? (
              <div className={styles.deleteConfirmation}>
                <p>Are you sure you want to delete Folder "{folderToEdit.name}" with all its contents?</p>
              </div>
            ) : (
              <>
                <label htmlFor="folderName">Folder Name <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  id="folderName"
                  name="folderName"
                  value={folderName}
                  onChange={handleFolderNameChange}
                  placeholder="Enter folder name..."
                  disabled={isSubmitting}
                  required
                  className={folderError ? styles.inputError : ''}
                />
                {folderError && <div className={styles.error}>{folderError}</div>}
              </>
            )}
          </div>
        </>
      )}

      <footer>
        <div className={styles.nav}>
          <div className={styles.cancel} onClick={closeFunc}>
            Cancel
          </div>
          <div 
            className={`${styles.accept} ${isFormValid ? styles.active : ''} ${isDelete ? styles.delete : ''}`}
            onClick={isFormValid ? handleSubmit : undefined}
          >
            {getSubmitButtonText()}
          </div>
        </div>
      </footer>
    </div>
  );
}; 